import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import { ReactSVG } from "react-svg";
import { useDispatch, useSelector } from "react-redux";

import {
  medReviewChecks,
  medicationReviewSteps,
  medicationReviewTypes,
  updateStepsValidity,
} from "../../../text/data";
import AccordionItem from "../../../../common/AccordionItem";
import EndServiceModel from "../../../../common/EndServiceModel";
import {
  createMedicationReview,
  setClinicalServiceSteps,
} from "../../../../../redux/modules/clinical-services/actions";
import CustomInput from "../../../../common/CustomInput";
import { MinorAilmentStep } from "../../../../../helpers/constant";
import NewToolTip from "../../../../common/New-ToolTip";

const OTHER_REASON_LABEL = "Other";

const MedicationReviewSymptomType = ({
  createNewClinicalService,
  setCreateNewClinicalService,
  setOpenNoSymptomMatchDialog,
  showMinorAilmentTooltip,
  setMinorAilmentsStep,
  setShowMinorAilmentTooltip,
  setAllowAilmentSelection,
  openNoSymptomMatchDialog,
  allowAilmentSelection,
  setMinorAilmentCreated,
  active,
  handleToggle,
  onReview,
  onContinue,
}) => {
  const dispatch = useDispatch();
  const { clinicalServiceObj, clinicalServiceLocal, clinicalServiceSteps } =
    useSelector((state) => state.clinicalService);
  const { patient } = useSelector((state) => state.patients);
  const [presentingSymptoms, setPresentingSymptoms] = useState("");
  const [onSymptomChanges, setOnSymptomChanges] = useState("Continue");
  const [comments, setcomments] = useState(null);
  const [showRef, setShowRef] = useState(
    clinicalServiceObj?.initialAssessment?.initialAssessmentForm ?? false
  );
  const [clickedEndService, setClickedEndService] = useState(false);
  const [ReviewChecks, setReviewChecks] = useState([]);
  const [otherCustomReason, setOtherCustomReason] = useState("");
  const primaryReasonChecks = medReviewChecks.slice(0, medReviewChecks.length - 3);
  const finalReasonChecks = medReviewChecks.slice(medReviewChecks.length - 3);

  useEffect(() => {
    if (clickedEndService) {
      onNext();
      setClickedEndService(false);
    }
  }, [clinicalServiceSteps, clickedEndService]);

  const updateClinicalService = () => {
    const updatedClinicalServiceObj = {
      ...clinicalServiceLocal, // Spread the existing properties of clinicalServiceObj
      preAssessment: {
        ...clinicalServiceLocal?.preAssessment,
        reasonsForMedicationReview: ReviewChecks,
        reasonsForMedicationReviewComments: comments,
      },
      currentStep: 1,
      currentSubStep: 1,
      startDateTime: new Date().toISOString(),
      endDateTime: new Date().toISOString(),
      showReferral: showRef,
      serviceSteps: medicationReviewSteps,
    };
    dispatch(
      createMedicationReview(
        patient.id,
        "medicationReview",
        updatedClinicalServiceObj
      )
    );
    setMinorAilmentCreated(true);
  };

  const handlePresentingSymptomsContinueClick = () => {
    onContinue(1);
    // const updatedClinicalServiceObj = {
    //   ...clinicalServiceObj, // Spread the existing properties of clinicalServiceObj
    //   preAssessment: {
    //     ...clinicalServiceObj.preAssessment, // Spread the existing properties of initialAssessment
    //     // presentingSymptoms: presentingSymptoms,
    //   },
    // };
    // dispatch({
    //   type: "UPDATE_CLINICAL_SERVICE_LOCAL",
    //   payload: updatedClinicalServiceObj,
    // });
  };

  useEffect(() => {
    onContinue(ReviewChecks.length === 0 ? 0 : 1);
  }, [ReviewChecks]);

  useEffect(() => {
    if (createNewClinicalService) {
      updateClinicalService();
      setCreateNewClinicalService(false);
    }
  }, [createNewClinicalService]);

  const handleChange = (e) => {
    if (e.target.checked) {
      setReviewChecks([...ReviewChecks, e.target.name]);
    } else {
      if (e.target.name === OTHER_REASON_LABEL) {
        setOtherCustomReason("");
      }
      setReviewChecks(ReviewChecks.filter((item) => item !== e.target.name));
    }
  };

  const updateStepsVisibility = async (disable = false) => {
    const updatedServiceSteps = updateStepsValidity(
      clinicalServiceSteps,
      currentStep,
      currentSubStep,
      disable
    );
    await dispatch(setClinicalServiceSteps(updatedServiceSteps));
  };

  const selectAllSourceChecks = () => {
    const excludedAutoSelectLabels = new Set([
      "initiate compliance packaging",
      "i had a recent discharge from hospital.",
      "i have a planned upcoming hospital admission.",
    ]);
    const updatedLabels = medReviewChecks
      .map((item) => item.label)
      // Keep last 3 options as manual-only selections.
      .filter(
        (label) => !excludedAutoSelectLabels.has(String(label).toLowerCase())
      );
    setReviewChecks(updatedLabels);
  };

  const compliancePackageTooltip = () => {
    return (
      <NewToolTip
        place="right"
        positionStrategy="fixed"
        id="package-tip"
        render={() => (
          <div className="!bg-gray-10 max-w-[460px] text-teal-300 !font-normal text-body-sm p-2">
            Identifies need for compliance packaging.
            <br />
            Initiate through your pharmacy’s existing process.
          </div>
        )}
      />
    );
  };

  return (
    <>
      <div>
        <button
          className="flex items-center gap-2 h-fit"
          onClick={() => {
            setMinorAilmentsStep(1);
            setAllowAilmentSelection(false);
          }}
        >
          <ChevronLeftIcon className="w-4 h-4 text-teal-300" />
          <h3 className="text-body-md font-semibold text-teal-300 hover:underline">
            Clinical Services
          </h3>
        </button>
      </div>
      <div
        className="flex items-center gap-3 "
        onMouseLeave={() => setShowMinorAilmentTooltip(false)}
      >
        <h4 className="text-2xl font-bold text-teal-300">
          Annual Medication Review
        </h4>
      </div>
      <div className="grid grid-cols-12 gap-5 items-stretch">
        <div className="col-span-3 flex h-full min-h-[min(72vh,42rem)] flex-col rounded-[4px] border border-gray-100">
          <div className="px-5 py-4 bg-gray-20">
            <h3 className="text-body-lg font-bold text-teal-300">
              Reason(s) for Medication Review
            </h3>
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto scroll-smooth">
            <div className="relative flex h-auto flex-col gap-5 p-5">
              <p className="font-semibold text-body-md text-gray-800">
                <span
                  onClick={() => selectAllSourceChecks()}
                  className="cursor-pointer ml-9 text-black underline underline-offset-2 font-medium"
                >
                  Select All
                </span>
              </p>
              {primaryReasonChecks.map((item, index) => (
                <div key={item.label}>
                  <span
                    key={index}
                    className="flex items-start text-body-md font-normal"
                    onFocus={() => {
                      setAllowAilmentSelection(false);
                      if (ReviewChecks.length > 0) {
                        allowAilmentSelection &&
                          setOnSymptomChanges("Save & Continue");
                      }
                    }}
                  >
                    {/* <span
                      className={``}
                    > */}
                    <input
                      type="checkbox"
                      id={index}
                      name={item.label}
                      checked={ReviewChecks.includes(item.label)}
                      onChange={handleChange}
                    />
                    <label
                      htmlFor={index}
                      className="ml-4 text-body-md font-normal text-gray-800"
                    >
                      {item.label}
                    </label>
                    {/* </span> */}
                  </span>
                </div>
              ))}
              <div className="mt-4 border-b border-gray-100"></div>
              {finalReasonChecks.map((item, index) => (
                <div key={item.label}>
                  <span
                    key={`final-${index}`}
                    className="flex items-start text-body-md font-normal"
                    onFocus={() => {
                      setAllowAilmentSelection(false);
                      if (ReviewChecks.length > 0) {
                        allowAilmentSelection &&
                          setOnSymptomChanges("Save & Continue");
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      id={`final-${index}`}
                      name={item.label}
                      checked={ReviewChecks.includes(item.label)}
                      onChange={handleChange}
                    />
                    <label
                      htmlFor={`final-${index}`}
                      className="ml-4 text-body-md font-normal text-gray-800"
                    >
                      {item.label}
                    </label>
                    {item.label === "Initiate compliance packaging" && (
                      <span
                        data-tooltip-id="package-tip"
                        data-tooltip-place="right"
                        className="ml-1 mt-1 inline-flex shrink-0 items-center"
                      >
                        <ReactSVG
                          src="/icons/information-dark.svg"
                          className="w-4 h-4"
                        />
                      </span>
                    )}
                  </span>
                  {item.label === "Initiate compliance packaging" &&
                    compliancePackageTooltip()}
                </div>
              ))}
              <div className="mt-4 border-b border-gray-100"></div>
              <div>
                <span
                  className="flex items-start text-body-md font-normal"
                  onFocus={() => {
                    setAllowAilmentSelection(false);
                    if (ReviewChecks.length > 0) {
                      allowAilmentSelection &&
                        setOnSymptomChanges("Save & Continue");
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    id="reason-other"
                    name={OTHER_REASON_LABEL}
                    checked={ReviewChecks.includes(OTHER_REASON_LABEL)}
                    onChange={handleChange}
                  />
                  <label
                    htmlFor="reason-other"
                    className="ml-4 text-body-md font-normal text-gray-800"
                  >
                    {OTHER_REASON_LABEL}
                  </label>
                </span>
                {ReviewChecks.includes(OTHER_REASON_LABEL) && (
                  <div className="mt-3">
                    <textarea
                      value={otherCustomReason}
                      onChange={(e) => setOtherCustomReason(e.target.value)}
                      placeholder="Add customized reason"
                      rows={3}
                      className="w-full min-h-[90px] resize-y rounded-[4px] border border-gray-100 px-3 py-2 text-body-md text-gray-800 placeholder:text-gray-400 focus:border-REELTeal focus:outline-none"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="relative mb-2 flex h-auto flex-col gap-6 px-5">
              <CustomInput
                name="comments"
                className="col-span-12 !h-[80px] [&_input]:!text-body-md [&_textarea]:!text-body-md"
                labelClassName="!text-body-md"
                label="Comments"
                placeholder="Comments"
                value={comments}
                onChange={(e) => setcomments(e.target.value)}
                type="text"
                onFocus={() => {
                  setAllowAilmentSelection(true);
                  if (ReviewChecks.length > 0) {
                    allowAilmentSelection &&
                      setOnSymptomChanges("Save & Continue");
                  }
                }}
              />
            </div>
          </div>
          {/* <div className="flex justify-end bg-gray-20 pr-3">
            <button
              className={`m-2 h-4 w-auto rounded-full bg-REELTeal border-REELTeal hover:bg-teal-300 text-white inline-flex border transition-all duration-300 items-center font-semibold justify-center px-3 py-4 ${
                ReviewChecks.length === 0
                  ? "pointer-events-none opacity-40"
                  : ""
              }`}
              onClick={() => handlePresentingSymptomsContinueClick()}
            >
              <h3 className="text-body-md font-semibold text-white">
                {onSymptomChanges}
              </h3>
              <ChevronRightIcon className="w-4 ml-2 h-4 text-white" />
            </button>
          </div> */}
        </div>
        <div
          className={`col-span-6 flex h-full min-h-[min(72vh,42rem)] w-[1000px] flex-col rounded-[4px] border border-gray-100
      ${
        !allowAilmentSelection &&
        "pointer-events-none cursor-not-allowed overflow-hidden !rounded-[10px] opacity-50"
      } `}
        >
          <div className="shrink-0 bg-gray-20 px-5 py-4">
            <h3 className="text-body-lg font-bold text-teal-300">
              Select type of Medication Review{" "}
            </h3>
          </div>
          <div className="bg-scroll min-h-0 flex-1 overflow-y-auto scroll-smooth text-body-md">
            {medicationReviewTypes.map((item, index) => (
              <div className="">
                <AccordionItem
                  key={index}
                  id={index}
                  faq={{ ...item, isClaimTooltip: true }}
                  active={active}
                  handleToggle={handleToggle}
                  onReview={onReview}
                  isAMR={true}
                  disable={item.disable}
                />
              </div>
            ))}
          </div>
          <div className="flex shrink-0 items-center justify-center px-5 py-6">
            {/* <button
              className="gap-2"
              onClick={() => setOpenNoSymptomMatchDialog(true)}
            >
              <h2 className="text-body-sm font-semibold text-teal-300">
                <u>No Symptoms Match Ailment(s)</u>
              </h2>
            </button> */}
          </div>
          <EndServiceModel
            open={openNoSymptomMatchDialog}
            showContinueWithNoRef={false}
            showContinueAndRef={false}
            noContinueButton={true}
            title="No Symptoms Match Alert"
            content={() => (
              <div className="text-gray-600">
                <p className="font-semibold text-md-body">
                  Presenting symptoms do not match a minor ailment.
                </p>
                <br />
                Unable to confirm patient's self-diagnosis.
              </div>
            )}
            handleClose={() => setOpenNoSymptomMatchDialog(false)}
            handleContinue={(ref) => {
              if (ref !== undefined) {
                setShowRef(ref);
              }
              setOpenNoSymptomMatchDialog(false);
              updateStepsVisibility(false);
            }}
            handleEndService={() => {
              setShowRef(true);
              setOpenNoSymptomMatchDialog(false);
              setMinorAilmentsStep(1);
              setOnSymptomChanges("Continue");
              setReviewChecks([]);
              setOtherCustomReason("");
              setcomments(null);
              updateStepsVisibility(true);
              setClickedEndService(true);
            }}
          />
        </div>
      </div>
    </>
  );
};

export default MedicationReviewSymptomType;
